<template>
  <div class="reg-step">
    <!-- Step banner + pro hint toggle -->
    <div v-if="showIntro" class="step-hint-bar">
      <div class="step-hint-left">
        <span class="hand-icon">👋</span>
        <div class="step-hint-text">
          <div class="step-hint-title">
            Step 1 of 5: name it + connect LinkedIn.
          </div>
          <div class="step-hint-desc">
            Just follow along — about 2 minutes to let AI start working for you!
          </div>
        </div>
      </div>
      <a-button
        class="pro-hint-btn"
        shape="round"
        size="small"
        @click="$emit('toggle-intro', !showIntro)"
      >
        I'm a pro, minimize hints
      </a-button>
    </div>

    <a-form-model
      ref="form"
      :model="form"
      :rules="rules"
      :label-col="{ span: 24 }"
      :wrapper-col="{ span: 24 }"
      class="reg-form"
    >
      <!-- Campaign Name -->
      <div class="form-block">
        <div class="block-title">
          <span class="title-icon">✅</span>
          <span>Campaign Name</span>
        </div>
        <a-form-model-item prop="campaignName">
          <a-input
            v-model="form.campaignName"
            placeholder="Please enter the campaign name"
            size="large"
          />
        </a-form-model-item>
        <div v-if="showIntro" class="block-tip">
          <span class="tip-icon">💡</span>
          <span class="tip-content" v-html="campaignNameTip" />
        </div>
      </div>

      <!-- Connect LinkedIn -->
      <div class="form-block">
        <div class="block-title">
          <span class="title-icon linkedin-icon">💼</span>
          <span>Connect Your LinkedIn Account</span>
          <a-icon type="question-circle" class="title-help-icon" />
        </div>

        <div class="safe-card">
          <div
            class="safe-card-header"
            @click="safeCardExpanded = !safeCardExpanded"
          >
            <span
              ><span class="shield-icon">🛡️</span> Why connect? Is it
              safe?</span
            >
            <a-icon :type="safeCardExpanded ? 'up' : 'down'" />
          </div>
          <div v-show="safeCardExpanded" class="safe-card-body">
            <div class="safe-intro" v-html="whyConnectIntro" />
            <div class="safe-grid">
              <div class="safe-item">
                <span class="safe-item-icon">🌐</span>
                <div class="safe-item-text">
                  <div class="safe-item-title">Dedicated proxy IP</div>
                  <div class="safe-item-desc">
                    Matches your usual login location to mimic a local login
                  </div>
                </div>
              </div>
              <div class="safe-item">
                <span class="safe-item-icon">🐢</span>
                <div class="safe-item-text">
                  <div class="safe-item-title">Human-like rate limits</div>
                  <div class="safe-item-desc">
                    Sends at a natural human pace to stay within limits
                  </div>
                </div>
              </div>
              <div class="safe-item">
                <span class="safe-item-icon">🔒</span>
                <div class="safe-item-text">
                  <div class="safe-item-title">Encrypted storage</div>
                  <div class="safe-item-desc">
                    Credentials are encrypted; nothing unrelated is ever done
                  </div>
                </div>
              </div>
              <div class="safe-item">
                <span class="safe-item-icon">🔓</span>
                <div class="safe-item-text">
                  <div class="safe-item-title">Disconnect anytime</div>
                  <div class="safe-item-desc">Revoke access with one click</div>
                </div>
              </div>
            </div>
            <a class="learn-more" href="javascript:void(0)"
              >Learn how we protect your account →</a
            >
          </div>
        </div>

        <a-input
          class="linkedin-input"
          v-model="form.linkedinEmail"
          placeholder="LinkedIn email"
          size="large"
          readonly
        >
          <a-icon slot="prefix" type="link" />
          <a-icon
            slot="suffix"
            type="check-circle"
            theme="twoTone"
            two-tone-color="#52c41a"
          />
        </a-input>
      </div>

      <!-- Daily Connection Request Limit -->
      <div class="form-block">
        <div class="block-title">
          <span class="title-icon chart-icon">📊</span>
          <span>Daily Connection Request Limit</span>
          <a-icon type="question-circle" class="title-help-icon" />
        </div>
        <div v-if="showIntro" class="block-tip">
          <span class="tip-icon">🌟</span>
          <span class="tip-content" v-html="dailyLimitTip" />
        </div>
        <div class="limit-row">
          <a-button
            class="limit-btn"
            shape="circle"
            :disabled="form.dailyLimit <= 1"
            @click="onLimitChange(-1)"
          >
            <a-icon type="minus" />
          </a-button>
          <a-input-number
            v-model="form.dailyLimit"
            class="limit-input"
            :min="1"
            :max="100"
            :controls="false"
          />
          <a-button
            class="limit-btn"
            shape="circle"
            :disabled="form.dailyLimit >= 100"
            @click="onLimitChange(1)"
          >
            <a-icon type="plus" />
          </a-button>
        </div>
      </div>

      <!-- Allocation Mode -->
      <div class="form-block">
        <div class="block-title">
          <span class="title-icon target-icon">🎯</span>
          <span>Select Allocation Mode</span>
        </div>
        <div v-if="showIntro" class="block-tip">
          <span class="tip-icon">🌟</span>
          <span class="tip-content" v-html="allocationModeTip" />
        </div>
        <allocation-options
          :allocationMode="form.allocationMode"
          :dailyLimit="form.dailyLimit"
          :activeCampaignsCount="activeCampaignsCount"
          @change="handleAllocationChange"
        />
      </div>
    </a-form-model>

    <!-- Action area: slot-based so parent can supply buttons -->
    <div class="form-actions">
      <slot
        name="actions"
        :submit="onSave"
        :cancel="onCancel"
        :saving="saving"
      />
    </div>
  </div>
</template>

<script>
import AllocationOptions from "@/components/allocationOptions/index.vue";

export default {
  name: "RegStep",
  components: {
    AllocationOptions,
  },
  props: {
    // bound linkedin account email
    linkedinAccount: {
      type: String,
      default: "",
    },
    initialCampaignName: {
      type: String,
      default: "",
    },
    initialDailyLimit: {
      type: Number,
      default: 25,
    },
    initialAllocationMode: {
      type: Number,
      default: 1,
      validator: (v) => v === 1 || v === 2,
    },
    activeCampaignsCount: {
      type: Number,
      default: 3,
    },
    // whether to show banner + tip blocks
    showIntro: {
      type: Boolean,
      default: true,
    },
    saving: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      safeCardExpanded: true,
      form: {
        campaignName: this.initialCampaignName,
        linkedinEmail: this.linkedinAccount,
        dailyLimit: this.initialDailyLimit,
        allocationMode: this.initialAllocationMode,
      },
      rules: {
        campaignName: [
          {
            required: true,
            message: "Please enter the campaign name",
            trigger: "blur",
          },
        ],
        linkedinEmail: [
          {
            required: true,
            message: "LinkedIn email is required",
            trigger: "blur",
          },
        ],
      },
      // Pre-rendered tip HTML strings (with bold keywords)
      campaignNameTip:
        'Pick a name that\'s easy to recognize — it <span class="tip-bold">makes managing multiple campaigns easier</span>; naming by role / region works best.',
      whyConnectIntro:
        'Connect LinkedIn to start automating outreach. Here\'s how we keep your account <span class="tip-bold">100% secure</span>:',
      dailyLimitTip:
        'LinkedIn applies stricter limits to new accounts. We recommend new accounts start at <span class="tip-bold">10–15 requests per day</span> and <span class="tip-bold">gradually increase after 1–2 months</span> of use.',
      allocationModeTip:
        'You can create <span class="tip-bold">multiple campaigns</span> under one LinkedIn account. Choose to run <span class="tip-bold">in creation order</span> (give all daily requests to the first campaign, then move to the next once it\'s done) or <span class="tip-bold">distribute evenly</span> (split the daily requests across all running campaigns at once). Set it however you like.',
    };
  },
  watch: {
    linkedinAccount(val) {
      this.form.linkedinEmail = val;
    },
    initialCampaignName(val) {
      this.form.campaignName = val;
    },
    initialDailyLimit(val) {
      this.form.dailyLimit = val;
    },
    initialAllocationMode(val) {
      this.form.allocationMode = val;
    },
  },
  methods: {
    handleAllocationChange(mode) {
      this.form.allocationMode = mode;
    },
    onLimitChange(delta) {
      const next = (this.form.dailyLimit || 0) + delta;
      if (next < 1 || next > 100) return;
      this.form.dailyLimit = next;
    },
    onSave() {
      const formRef = this.$refs.form;
      if (!formRef || typeof formRef.validate !== "function") {
        this.$emit("submit", { ...this.form });
        return;
      }
      formRef.validate((valid) => {
        if (valid) {
          this.$emit("submit", { ...this.form });
        } else {
          return false;
        }
      });
    },
    onCancel() {
      this.$emit("cancel");
    },
  },
};
</script>

<style scoped lang="less">
// ============ Variables ============
@primary: #0e756a;
@primary-dark: #0a5d54;
@hint-bg: #f6ffed;
@hint-border: #b7eb8f;
@hint-soft: #f3fbf8;
@title-color: #1f1f1f;
@muted-color: rgba(0, 0, 0, 0.45);
@muted-strong: rgba(0, 0, 0, 0.65);
@muted-soft: rgba(0, 0, 0, 0.55);
@border-soft: #d9d9d9;
@bg-soft: #fafafa;
@linkedin-blue: #0a66c2;
@chart-purple: #722ed1;
@target-orange: #fa8c16;

// ============ Root container ============
.reg-step {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px;

  // ===== Top step hint bar =====
  .step-hint-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
    padding: 12px 18px;
    margin-bottom: 28px;
    background: @hint-soft;
    border-radius: 8px;

    .step-hint-left {
      display: flex;
      align-items: center;
      gap: 12px;

      .hand-icon {
        font-size: 22px;
      }

      .step-hint-text {
        display: flex;
        flex-direction: column;
        gap: 2px;

        .step-hint-title {
          font-size: 14px;
          font-weight: 600;
          color: @title-color;
        }

        .step-hint-desc {
          font-size: 12px;
          color: @muted-strong;
        }
      }
    }

    .pro-hint-btn {
      font-size: 13px;
      color: @title-color;
      background: #fff;
      border-color: @border-soft;
    }
  }

  // ===== Form blocks =====
  .form-block {
    margin-bottom: 28px;

    .block-title {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 12px;
      font-size: 18px;
      font-weight: 600;
      color: @title-color;

      @media (max-width: 600px) {
        font-size: 14px;
        margin-bottom: 4px;
      }
    }

    .title-icon {
      font-size: 20px;

      &.linkedin-icon {
        color: @linkedin-blue;
      }

      &.chart-icon {
        color: @chart-purple;
      }

      &.target-icon {
        color: @target-orange;
      }
    }

    .title-help-icon {
      font-size: 14px;
      color: @muted-color;
      cursor: pointer;
    }

    // Antd input sizing override (scoped deep)
    .reg-form {
      /deep/.ant-input-affix-wrapper-lg,
      /deep/.ant-input-lg {
        font-size: 15px;
      }
    }

    // Tip line under input
    .block-tip {
      display: flex;
      gap: 6px;
      margin-top: 8px;
      font-size: 13px;
      line-height: 1.6;
      color: @muted-strong;

      .tip-icon {
        flex-shrink: 0;
        font-size: 14px;
      }

      .tip-content {
        flex: 1;
      }

      :deep(.tip-bold) {
        font-weight: 600;
        color: @title-color;
      }
    }

    // ===== Safe card (Why connect?) =====
    .safe-card {
      overflow: hidden;
      margin-bottom: 16px;
      background: @hint-bg;
      border-radius: 8px;

      .safe-card-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 14px 16px;
        font-size: 15px;
        font-weight: 600;
        color: @title-color;
        cursor: pointer;

        .shield-icon {
          margin-right: 6px;
        }
      }

      .safe-card-body {
        padding: 0 16px 16px;

        .safe-intro {
          margin-bottom: 14px;
          font-size: 13px;
          line-height: 1.6;
          color: @muted-strong;
        }

        .safe-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px 24px;
          margin-bottom: 12px;

          @media (max-width: 600px) {
            grid-template-columns: 1fr;
          }
        }

        .safe-item {
          display: flex;
          gap: 10px;
          align-items: flex-start;

          .safe-item-icon {
            flex-shrink: 0;
            font-size: 18px;
            line-height: 1.4;
          }

          .safe-item-text {
            .safe-item-title {
              margin-bottom: 2px;
              font-size: 14px;
              font-weight: 600;
              color: @title-color;
            }

            .safe-item-desc {
              font-size: 12px;
              line-height: 1.4;
              color: @muted-soft;
            }
          }
        }

        .learn-more {
          font-size: 13px;
          font-weight: 600;
          color: @primary;
        }
      }
    }

    // ===== LinkedIn email input =====
    .linkedin-input {
      margin-top: 8px;

      /deep/.ant-input-affix-wrapper {
        background: @bg-soft;
      }
    }

    // ===== Daily Limit row =====
    .limit-row {
      display: flex;
      align-items: center;
      gap: 16px;
      margin-top: 12px;

      .limit-btn {
        border-color: @border-soft;
      }

      .limit-input {
        width: 100px;

        /deep/.ant-input-number-input {
          height: 40px;
          font-size: 22px;
          font-weight: 600;
          text-align: center;
        }
      }
    }
  }

  // ===== Action buttons =====
  .form-actions {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 32px;

    .save-btn {
      padding: 0 32px;
      font-weight: 500;
      color: #fff;
      background: @primary;
      border-color: @primary;

      &:hover,
      &:focus {
        background: @primary-dark;
        border-color: @primary-dark;
      }
    }

    .cancel-btn {
      padding: 0 24px;
    }
  }
}
</style>
